import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, KeyRound, ArrowLeft, Smartphone, HelpCircle, X, Download, ScanLine, CheckCircle2 } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/* ── FUNDIO LOGIN PREMIUM V3 — local color lock (only /login) ── */
/* Heading font matches Control OS master: source renders login headings in Satoshi Black (900),
   NOT Cabinet Grotesk (source never loads Cabinet). Weight 800 resolves to the self-hosted Satoshi 900 face. */
const FONT_HEAD = '"Satoshi",sans-serif';
const FONT_BODY = '"Satoshi",sans-serif';
const C = {
  navy: "#0E141B",
  surface: "#161D25",
  border: "#303A44",
  gold: "#D89A00",
  goldHover: "#E7B515",
  goldPressed: "#8C5A00",
  offwhite: "#F7F5F0",
  grey: "#A6ADB5",
  muted: "#777F88",
};

const inputCls = "mt-1.5 w-full h-[48px] rounded-[12px] border border-[#303A44] bg-[#161D25] px-4 text-[14px] text-[#F7F5F0] outline-none transition-colors focus:border-[#E7B515] placeholder:text-[#777F88]";
const inputStyle = { fontFamily: FONT_BODY };
const codeCls = "mt-4 w-full h-[58px] rounded-[12px] border border-[#303A44] bg-[#161D25] px-4 text-center text-2xl tracking-[0.5em] text-[#F7F5F0] outline-none transition-colors focus:border-[#E7B515]";
const btnCls = "mt-6 flex w-full items-center justify-center gap-2 h-[48px] rounded-[12px] bg-[#161D25] border border-[#D89A00] text-[14px] font-medium text-[#F7F5F0] transition-colors hover:bg-[#19212A] hover:border-[#E7B515] active:bg-[#0E141B] disabled:opacity-60";
const btnStyle = { fontFamily: FONT_BODY, fontSize: "14px", fontWeight: 500 };
const labelCls = "mt-6 block text-[11px] font-medium uppercase text-[#A6ADB5]";
const labelStyle = { fontFamily: FONT_BODY, letterSpacing: "0.06em" };
const titleStyle = { fontFamily: FONT_HEAD, fontWeight: 900, lineHeight: 1.05, color: C.offwhite };
const subtitleStyle = { fontFamily: FONT_BODY, fontSize: "14px", color: C.grey };
const formEyebrowStyle = { fontFamily: FONT_BODY, fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.18em", color: "#E7B515" };
const securityStyle = { marginTop: "24px", fontFamily: FONT_BODY, fontSize: "12px", color: C.grey };

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const AUTH_APPS = [
  { name: "Google Authenticator", url: "https://support.google.com/accounts/answer/1066447" },
  { name: "Microsoft Authenticator", url: "https://www.microsoft.com/security/mobile-authenticator-app" },
  { name: "Authy", url: "https://authy.com/download/" },
];

function TwoFAHelp({ onClose }) {
  const steps = [
    { icon: Download, title: "1. Authenticator-App installieren", body: "Lade auf deinem Handy eine kostenlose Authenticator-App aus dem App Store oder Play Store. Wir empfehlen eine der folgenden:" },
    { icon: ScanLine, title: "2. QR-Code scannen", body: "Öffne die App, tippe auf „+\" bzw. „Konto hinzufügen\" → „QR-Code scannen\" und halte die Kamera auf den Code hier auf dem Bildschirm. Kein Scanner? Gib den angezeigten Schlüssel manuell ein." },
    { icon: CheckCircle2, title: "3. 6-stelligen Code eingeben", body: "Die App zeigt nun alle 30 Sekunden einen neuen 6-stelligen Code. Tippe den aktuellen Code unten ein – fertig! Beim nächsten Login brauchst du nur noch deinen aktuellen Code." },
  ];
  return (
    <div data-testid="twofa-help-overlay" className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border p-6 shadow-2xl" style={{ backgroundColor: C.surface, borderColor: C.border }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h2 className="flex items-center gap-2 text-lg font-extrabold" style={{ fontFamily: FONT_HEAD, color: C.offwhite }}><Smartphone size={18} style={{ color: C.gold }} /> So richtest du 2FA ein</h2>
          <button data-testid="twofa-help-close" onClick={onClose} className="rounded-full p-1 hover:bg-black/30" style={{ color: C.grey }}><X size={18} /></button>
        </div>
        <p className="mt-1 text-sm" style={{ color: C.grey }}>Zwei-Faktor-Schutz ist Pflicht und schützt dein Konto auch dann, wenn jemand dein Passwort kennt.</p>
        <div className="mt-5 space-y-4">
          {steps.map((s) => (
            <div key={s.title} className="flex gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: "rgba(201,151,47,0.15)", color: C.gold }}><s.icon size={18} /></span>
              <div>
                <p className="text-sm font-bold" style={{ color: C.offwhite }}>{s.title}</p>
                <p className="mt-0.5 text-sm leading-relaxed" style={{ color: C.grey }}>{s.body}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {AUTH_APPS.map((a) => (
            <a key={a.name} data-testid={`twofa-help-app-${a.name.split(" ")[0].toLowerCase()}`} href={a.url} target="_blank" rel="noreferrer"
              className="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium hover:border-[#D89A00]" style={{ backgroundColor: C.navy, borderColor: C.border, color: C.offwhite }}>
              <Download size={13} /> {a.name}
            </a>
          ))}
        </div>
        <button data-testid="twofa-help-done" onClick={onClose} className="mt-5 w-full rounded-lg py-2.5 text-sm font-medium transition-colors hover:bg-[#E7B515]" style={{ backgroundColor: C.gold, color: C.navy }}>Verstanden, los geht's</button>
      </div>
    </div>
  );
}

export default function Login() {
  const { user, applyToken } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState("password"); // password | change_password | setup_2fa | twofa
  const [showHelp, setShowHelp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [code, setCode] = useState("");
  const [tempToken, setTempToken] = useState(null);
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(false);
  const turnstileRef = useRef(null);

  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);

  // One-time password reset: /admin/login?reset=<token> → jump straight to "set new password"
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get("reset");
    if (!resetToken) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/auth/reset/consume", { token: resetToken });
        setTempToken(data.temp_token);
        setStep("change_password");
        setError("");
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || "Dieser Reset-Link ist ungültig oder abgelaufen.");
      } finally {
        setLoading(false);
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const init2faSetup = async (tt) => {
    try {
      const { data } = await api.post("/auth/challenge/2fa-init", { temp_token: tt });
      setQr(data.qr); setSecret(data.secret); setCode(""); setStep("setup_2fa");
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || "Fehler bei der 2FA-Einrichtung"); }
  };

  const handleAuth = (data) => {
    if (data.token) { applyToken(data.token, data.user); navigate("/dashboard"); return; }
    setError("");
    if (data.challenge === "password") { setTempToken(data.temp_token); setStep("change_password"); }
    else if (data.requires_2fa || data.challenge === "2fa") { setTempToken(data.temp_token); setCode(""); setStep("twofa"); }
    else if (data.challenge === "setup_2fa") { setTempToken(data.temp_token); setShowHelp(true); init2faSetup(data.temp_token); }
    else { setError("Unerwartete Antwort vom Server. Bitte erneut versuchen."); }
  };

  const submitPassword = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, turnstile_token: captchaToken });
      if (data.requires_captcha) { setRequiresCaptcha(true); setError("Bitte bestätige unten, dass du kein Roboter bist."); return; }
      handleAuth(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Anmeldung fehlgeschlagen");
      if (requiresCaptcha) { turnstileRef.current?.reset(); setCaptchaToken(null); }
    } finally { setLoading(false); }
  };

  const submitNewPw = async (e) => {
    e.preventDefault(); setError("");
    if (newPw !== newPw2) { setError("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try { const { data } = await api.post("/auth/challenge/password", { temp_token: tempToken, new_password: newPw }); handleAuth(data); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || "Passwort konnte nicht gesetzt werden"); }
    finally { setLoading(false); }
  };

  const submitSetup2fa = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { const { data } = await api.post("/auth/challenge/2fa-complete", { temp_token: tempToken, code }); handleAuth(data); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || "Code ungültig"); }
    finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try { const { data } = await api.post("/auth/2fa/login", { temp_token: tempToken, code }); handleAuth(data); }
    catch (e) { setError(formatApiError(e.response?.data?.detail) || "Code ungültig"); }
    finally { setLoading(false); }
  };

  const backToPassword = () => { setStep("password"); setError(""); setCode(""); setNewPw(""); setNewPw2(""); setQr(null); };

  const errBox = (tid) => error && <div data-testid={tid} className="mt-4 rounded-lg bg-red-500/15 px-3 py-2.5 text-sm text-red-300">{error}</div>;

  return (
    <div className="dark min-h-screen lg:grid lg:grid-cols-2" style={{ backgroundColor: C.navy, color: C.offwhite }}>
      {/* LEFT — hero (desktop, 50vw) */}
      <div data-testid="login-hero" className="relative hidden overflow-hidden lg:block" style={{ minHeight: "100vh" }}>
        <img src="/login-hero.png" alt="Fundio Brockenhaus" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "center", filter: "grayscale(1) brightness(0.7) contrast(1.06)" }} />
        {/* Control OS blue-black overlay (identical treatment) */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(9,13,18,0.72) 0%, rgba(14,20,27,0.85) 55%, rgba(22,29,37,0.95) 100%)" }} />
        {/* Logo — WEISS/GOLD master, 175px, top 42px, centered */}
        <img data-testid="login-hero-logo" src="/fundio-logo-white.png" alt="Fundio" draggable="false"
          className="absolute left-1/2 -translate-x-1/2 select-none object-contain" style={{ top: "46px", width: "240px", height: "auto" }} />
        {/* Hero text block — left 48px, bottom 62px, max-width 520px */}
        <div className="fundio-reveal absolute" style={{ left: "48px", bottom: "70px", maxWidth: "520px" }}>
          <p style={{ fontFamily: FONT_BODY, fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.20em", color: "#E7B515" }}>{t("login.eyebrow")}</p>
          <h2 data-testid="login-hero-claim" className="mt-[18px]" style={{ fontFamily: FONT_HEAD, fontSize: "46px", fontWeight: 800, lineHeight: 0.98, letterSpacing: "-0.025em", color: C.offwhite }}>
            {t("login.hero_claim")}
          </h2>
          <p className="mt-5" style={{ fontFamily: FONT_BODY, fontSize: "15px", fontWeight: 400, lineHeight: 1.55, maxWidth: "450px", color: C.grey }}>
            {t("login.hero_sub")}
          </p>
        </div>
      </div>

      {/* RIGHT — form (50vw), premium navy with subtle radial glow */}
      <div data-testid="login-form-pane" className="relative flex min-h-screen flex-col items-center justify-center px-6 py-12 lg:px-16"
        style={{ backgroundColor: C.navy }}>
        <div className="absolute right-5 top-5"><LanguageSwitcher variant="light" persist={false} /></div>
        <div data-testid="login-form-card" className="fundio-reveal" style={{ width: "352px", maxWidth: "calc(100vw - 48px)" }}>
          {/* Mobile logo — 190px, centered */}
          <div className="mb-8 flex justify-center lg:hidden">
            <img data-testid="login-mobile-logo" src="/fundio-logo-white.png" alt="Fundio" draggable="false" className="select-none object-contain" style={{ width: "190px", height: "auto" }} />
          </div>

          <p className="mb-3" style={formEyebrowStyle}>{t("login.eyebrow")}</p>

          {step === "password" && (
            <form onSubmit={submitPassword}>
              <h1 data-testid="login-title" className="text-[30px]" style={titleStyle}>{t("login.title")}</h1>
              <p className="mt-1.5" style={subtitleStyle}>{t("login.subtitle")}</p>
              {errBox("login-error")}
              <label className={labelCls} style={labelStyle}>{t("login.email")}</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} style={inputStyle} placeholder="name@brocki.ch" />
              <label className={`${labelCls} !mt-4`} style={labelStyle}>{t("login.password")}</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={inputCls} style={inputStyle} placeholder="••••••••" />
              {requiresCaptcha && SITE_KEY && (
                <div className="mt-4 flex flex-col items-center gap-2" data-testid="turnstile-widget">
                  <Turnstile ref={turnstileRef} siteKey={SITE_KEY} onSuccess={(t2) => { setCaptchaToken(t2); setCaptchaError(false); }} onExpire={() => setCaptchaToken(null)} onError={() => { setCaptchaToken(null); setCaptchaError(true); }} options={{ theme: "dark" }} />
                  {captchaError && (
                    <button type="button" data-testid="captcha-reload" onClick={() => window.location.reload()} className="text-xs underline" style={{ color: C.gold }}>
                      {t("login.captcha_reload")}
                    </button>
                  )}
                </div>
              )}
              <button data-testid="login-submit" disabled={loading || (requiresCaptcha && !captchaToken)} className={btnCls} style={btnStyle}>{loading && <Loader2 size={16} className="animate-spin" />} {t("login.submit")}</button>
            </form>
          )}

          {step === "change_password" && (
            <form onSubmit={submitNewPw}>
              <button type="button" onClick={backToPassword} className="mb-3 flex items-center gap-1.5 text-xs hover:opacity-80" style={{ color: C.grey }}><ArrowLeft size={13} /> {t("common.back")}</button>
              <h1 className="flex items-center gap-2 text-[28px] lg:text-[32px]" style={titleStyle}><KeyRound size={18} style={{ color: C.gold }} /> {t("login.changepw_title")}</h1>
              <p className="mt-1.5" style={subtitleStyle}>{t("login.changepw_subtitle")}</p>
              {errBox("changepw-error")}
              <label className={labelCls} style={labelStyle}>{t("login.changepw_new")}</label>
              <input data-testid="changepw-new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required className={inputCls} style={inputStyle} placeholder="••••••••" />
              <label className={labelCls} style={labelStyle}>{t("login.changepw_confirm")}</label>
              <input data-testid="changepw-confirm" type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} required className={inputCls} style={inputStyle} placeholder="••••••••" />
              <button data-testid="changepw-submit" disabled={loading} className={btnCls} style={btnStyle}>{loading && <Loader2 size={16} className="animate-spin" />} {t("login.changepw_submit")}</button>
            </form>
          )}

          {step === "setup_2fa" && (
            <form onSubmit={submitSetup2fa}>
              <button type="button" onClick={backToPassword} className="mb-3 flex items-center gap-1.5 text-xs hover:opacity-80" style={{ color: C.grey }}><ArrowLeft size={13} /> {t("common.back")}</button>
              <h1 className="flex items-center gap-2 text-[28px] lg:text-[32px]" style={titleStyle}><Smartphone size={18} style={{ color: C.gold }} /> {t("login.setup2fa_title")}</h1>
              <p className="mt-1.5" style={subtitleStyle}>{t("login.setup2fa_subtitle")}</p>
              <button type="button" data-testid="twofa-help-open" onClick={() => setShowHelp(true)} className="mt-2 flex items-center gap-1.5 text-xs font-medium hover:underline" style={{ color: C.gold }}><HelpCircle size={14} /> {t("login.setup2fa_help")}</button>
              {qr ? (
                <div className="mt-4 flex flex-col items-center">
                  <img data-testid="setup-2fa-qr" src={qr} alt="2FA QR-Code" className="h-44 w-44 rounded-lg bg-white p-2" />
                  {secret && <p className="mt-2 break-all text-center text-[11px]" style={{ color: C.muted }}>{t("login.setup2fa_key")} <span className="font-mono" style={{ color: C.grey }}>{secret}</span></p>}
                </div>
              ) : <div className="mt-4 flex justify-center"><Loader2 className="animate-spin" style={{ color: C.gold }} /></div>}
              {errBox("setup2fa-error")}
              <input data-testid="setup-2fa-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" className={codeCls} placeholder="000000" />
              <button data-testid="setup-2fa-submit" disabled={loading || code.length < 6} className={btnCls} style={btnStyle}>{loading && <Loader2 size={16} className="animate-spin" />} {t("login.setup2fa_submit")}</button>
            </form>
          )}

          {step === "twofa" && (
            <form onSubmit={submitCode}>
              <button type="button" onClick={backToPassword} className="mb-3 flex items-center gap-1.5 text-xs hover:opacity-80" style={{ color: C.grey }}><ArrowLeft size={13} /> {t("common.back")}</button>
              <h1 className="flex items-center gap-2 text-[28px] lg:text-[32px]" style={titleStyle}><KeyRound size={18} style={{ color: C.gold }} /> {t("login.twofa_title")}</h1>
              <p className="mt-1.5" style={subtitleStyle}>{t("login.twofa_subtitle")}</p>
              {errBox("twofa-error")}
              <input data-testid="twofa-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoFocus className={codeCls} placeholder="000000" />
              <button data-testid="twofa-submit" disabled={loading || code.length < 6} className={btnCls} style={btnStyle}>{loading && <Loader2 size={16} className="animate-spin" />} {t("login.twofa_submit")}</button>
            </form>
          )}

          <p className="flex items-center gap-1.5" style={securityStyle}><ShieldCheck size={13} /> {t("login.security_footer")}</p>
        </div>
      </div>
      {showHelp && <TwoFAHelp onClose={() => setShowHelp(false)} />}
    </div>
  );
}
